import { Link, useParams } from "react-router-dom";
import { MarketingShell } from "../components/MarketingShell.js";
import { usePageMeta } from "../lib/use-page-title.js";
import { JsonLd } from "../components/JsonLd.js";
import { ShareRow } from "../components/ShareRow.js";
import posts from "../data/blog-posts.json";

/**
 * A single journal post.
 *
 * Body HTML is generated at build time by scripts/build-blog.mjs from our own
 * markdown (parsed, figure-expanded, and passed through the blocking voice gate in
 * scripts/blog_gate.py). It is our content, never user input, so injecting it is
 * safe; the prose styling lives in .bht-prose so it uses the same design tokens as
 * the rest of the app instead of a private stylesheet.
 */

type Post = {
  slug: string;
  title: string;
  description: string;
  dateHuman: string;
  category: string;
  minutes: number;
  ledeHtml: string;
  gistHtml?: string[] | null;
  bodyHtml: string;
  ctaHtml: string;
  related: { slug: string; title: string }[];
  jsonld: unknown;
};

const BY_SLUG = posts as unknown as Record<string, Post>;

export function BlogPost() {
  const { slug = "" } = useParams();
  const post = BY_SLUG[slug];

  // Every post has a hand-written description; before this they all shipped the
  // homepage's, so a shared link previewed as the generic marketing page.
  usePageMeta({
    title: post ? post.title : "Journal",
    description: post ? post.description : "Break the Test journal.",
    path: post ? `/blog/${post.slug}/` : "/blog/",
    type: "article",
    image: post ? `/og/${post.slug}.png` : "/og/blog.png",
  });

  if (!post) {
    return (
      <MarketingShell>
        <div style={{ maxWidth: 700, margin: "0 auto", padding: "64px 24px 80px" }}>
          <h1 style={{ fontFamily: "var(--font-sans)", fontSize: 26, color: "var(--fg-0)", margin: 0 }}>
            That post does not exist
          </h1>
          <p style={{ fontFamily: "var(--font-sans)", color: "var(--fg-1)", lineHeight: 1.6 }}>
            It may have been renamed. The full list is on the journal index.
          </p>
          <Link to="/blog/" style={backLink}>
            All posts
          </Link>
        </div>
      </MarketingShell>
    );
  }

  return (
    <MarketingShell>
      <JsonLd data={post.jsonld} />
      <div style={{ maxWidth: 700, margin: "0 auto", padding: "44px 24px 72px" }}>
        <article className="bht-prose">
          <div className="bht-prose__cat">{post.category}</div>
          <h1>{post.title}</h1>
          <p className="bht-prose__meta">
            {post.minutes} min read &middot; {post.dateHuman}
          </p>
          {/* The post's own summary, set apart so the article's start is visible.
              When the post carries a gist, the label states the time it costs to
              read: a student deciding whether to stay is deciding about time, and
              a number answers that better than the word "Summary" does. */}
          <div className="bht-prose__summary">
            <div className="bht-prose__summary-label">
              {post.gistHtml?.length ? "In 30 seconds" : "Summary"}
            </div>
            {post.gistHtml?.length ? (
              /* The gist REPLACES the one-line lede rather than sitting under it.
                 Showing both put the same dates on screen twice inside one card,
                 and the body's opening paragraph then made it three times. The
                 description still does its real jobs: search snippet, index card
                 and meta tag. It just does not need to be read aloud here too. */
              <ul className="bht-prose__gist">
                {post.gistHtml.map((g, i) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: g }} />
                ))}
              </ul>
            ) : (
              <p className="bht-prose__lede" dangerouslySetInnerHTML={{ __html: post.ledeHtml }} />
            )}
          </div>
          <div dangerouslySetInnerHTML={{ __html: post.bodyHtml }} />
          <div dangerouslySetInnerHTML={{ __html: post.ctaHtml }} />
        </article>
        <ShareRow path={`/blog/${post.slug}/`} title={post.title} />
        {/* Outside <article>: "read next" is navigation about the page, not part
            of the piece. .bht-prose__rel is a top-level selector, so it styles
            correctly here. */}
        {post.related.length > 0 && (
          <nav className="bht-prose__rel" aria-label="Related posts">
            <h3>Read next</h3>
            <ul>
              {post.related.map((r) => (
                <li key={r.slug}>
                  <Link to={`/blog/${r.slug}/`}>{r.title}</Link>
                </li>
              ))}
            </ul>
          </nav>
        )}
        <p style={{ marginTop: 28 }}>
          <Link to="/blog/" style={backLink}>
            All posts
          </Link>
        </p>
      </div>
    </MarketingShell>
  );
}

const backLink: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--accent-amber)",
  textDecoration: "none",
};
